<template>
  <Card v-if="isPayCompleted">
    <VRow>
      <VCol>
        <ThankYou v-if="isFoundApplication">
          <template v-slot:title>
            <span class="text-main-color">Thanks!</span> We’re preparing your file <br />
            and will share your login details soon.
          </template>
          <template v-slot:text>
            You should get an email within the next hour with your <br />
            Pension Keeper file. <i>(Check your spam folder too)</i>.
          </template>
        </ThankYou>
        <ThankYou v-else>
          <template v-slot:title>
            <span class="text-main-color">Thanks!</span> We need to ask you <br />
            some questions before we proceed.
          </template>
          <template v-slot:text>
            You should get an email within the next hour with a form to <br />
            complete your Pension Keeper file. <i>(Check your spam folder too)</i>.
          </template>
        </ThankYou>
      </VCol>
    </VRow>
  </Card>

  <template v-else>
    <h1 class="text-main-color my-3">Welcome to Our Pension Keepers Service</h1>
    <Card>
      <VRow>
        <VCol cols="12">
          <div class="d-flex flex-column align-center justify-center w-100 ga-3">
            <h1 class="text-title-color font-size-18 font-weight-700">Payment details</h1>
            <div class="text-subtitle-color font-size-14 font-weight-500">Complete your sign-up securely.</div>
          </div>
        </VCol>
        <VCol v-if="!isCountrySelected" cols="12">
          <div class="d-flex flex-column align-center justify-center ga-2">
            <div class="text-subtitle-color font-size-14 font-weight-500">Click where you live now:</div>
            <VRow>
              <template v-for="country of countryList" :key="country.name">
                <VCol cols="12" sm="4">
                  <div
                    @click="handleSelectCountry(country)"
                    class="d-flex flex-column align-center justify-center country-list-container__item"
                  >
                    <img :src="country.image" :alt="country.name" />
                    <div>{{ country.name }}</div>
                  </div>
                </VCol>
              </template>
            </VRow>
          </div>
        </VCol>
      </VRow>
      <template v-if="isCountrySelected">
        <template v-if="isStripeLoaded">
          <VForm v-model="formValid" @submit.prevent>
            <VRow>
              <VCol cols="12">
                <VTextField
                  label="Email"
                  variant="outlined"
                  color="primary"
                  name="email"
                  type="email"
                  placeholder="e.g. name@example.com"
                  v-model="email"
                  :disabled="isPayLoading"
                  :rules="formRules.email"
                  class="custom-input"
                />
              </VCol>
              <VCol cols="12">
                <div class="text-title-color font-size-16 font-weight-600 mb-3">Personal Details</div>
                <div id="address-element"></div>
              </VCol>
              <VCol cols="12">
                <label>Card Number</label>
                <div id="card-element"></div>
              </VCol>
              <VCol cols="12" class="mt-3">
                <div>
                  <VTextarea
                    v-model.trim="howDidYouHearAboutUs"
                    label="How did you hear about us?"
                    variant="outlined"
                    maxlength="255"
                    :disabled="isPayLoading"
                    class="custom-input"
                  >
                  </VTextarea>
                </div>
              </VCol>
              <VCol cols="12">
                <div>
                  <VSelect
                    label="Which Agent Did You Speak to?"
                    :items="agentsOptions"
                    v-model="agent"
                    variant="outlined"
                    :disabled="isPayLoading"
                    class="custom-input"
                  >
                  </VSelect>
                </div>
              </VCol>
              <VCol cols="12">
                <div class="d-flex flex-column align-center justify-center">
                  <VCheckbox v-model="isAgreeTermsConditions" :disabled="isPayLoading" :rules="formRules.terms">
                    <template v-slot:label>
                      <div>
                        I agree to XtraPension's
                        <a href="https://www.xtrapension.com/terms" target="_blank" v-bind="props" @click.stop>
                          Terms & Conditions</a
                        >
                      </div>
                    </template>
                  </VCheckbox>
                </div>
              </VCol>
              <VCol cols="12">
                <div v-if="isPayLoading" class="d-flex flex-column align-center justify-center py-4 w-100">
                  <VProgressCircular indeterminate color="primary" />
                </div>
                <div class="d-flex items-center justify-center w-100">
                  <button
                    class="main-btn"
                    :disabled="isPayLoading || !isAgreeTermsConditions"
                    @click="pay()"
                    type="submit"
                  >
                    Pay {{ currencySymbol }}{{ formatAmount(amount) }}
                  </button>
                </div>
                <div v-if="stripeValidationError" class="info-container info-container__red my-2">
                  <p>{{ stripeValidationError }}</p>
                </div>
              </VCol>
            </VRow>
          </VForm>
        </template>
        <template v-else>
          <div class="d-flex flex-column align-center justify-center py-4 w-100">
            <VProgressCircular indeterminate color="primary" />
          </div>
        </template>
      </template>
    </Card>
  </template>
</template>
<script>
import { mapActions } from 'vuex';
// Service
import StripeService from '@/services/stripe-service/stripe.service';
import applicationsService from '@/services/applications-service/applications.service';
// Componens
import Card from '../Card/Card.vue';
import ThankYou from '../ThankYou/ThankYou.vue';
export default {
  inject: ['agentsOptions'],
  components: {
    Card,
    ThankYou
  },
  props: {
    foundApplication: {
      type: Object
    }
  },
  computed: {
    currencyCode() {
      let currency = 'USD';
      if (this.isEuropeanCountry(this.countrySelected)) {
        currency = 'EUR';
      } else if (this.countrySelected === 'CA') {
        currency = 'CAD';
      } else if (this.countrySelected === 'US') {
        currency = 'USD';
      } else if (this.countrySelected === 'AU') {
        currency = 'AUD';
      } else if (this.countrySelected === 'NZ') {
        currency = 'NZD';
      } else if (this.countrySelected === 'ZA') {
        currency = 'ZAR';
      }
      return currency;
    },
    currencySymbol() {
      let currencySymbol = 'US $';
      if (this.isEuropeanCountry(this.countrySelected)) {
        currencySymbol = '€';
      } else if (this.countrySelected === 'CA') {
        currencySymbol = 'CA $';
      } else if (this.countrySelected === 'AU') {
        currencySymbol = 'AU $';
      } else if (this.countrySelected === 'NZ') {
        currencySymbol = 'NZ $';
      } else if (this.countrySelected === 'ZA') {
        currencySymbol = 'R';
      }
      return currencySymbol;
    },
    amount() {
      let amount = 15000;
      if (this.isEuropeanCountry(this.countrySelected)) {
        amount = 12500;
      } else if (this.countrySelected === 'AU' || this.countrySelected === 'NZ' || this.countrySelected === 'CA') {
        amount = 20000;
      } else if (this.countrySelected === 'ZA') {
        amount = 200000;
      }
      return amount;
    }
  },
  data() {
    return {
      isStripeLoaded: false,
      isPayLoading: false,
      isPayCompleted: false,
      isCountrySelected: false,
      countrySelected: 'Canada',
      payCompletedId: null,
      isAppCreateLoading: false,
      isAppCreateError: false,
      stripe: null,
      cardElement: null,
      addressElement: null,
      fullName: null,
      firstName: null,
      lastName: null,
      shippingAddress: null,
      phone: null,
      email: '',
      stripeValidationError: '',
      tax: true,
      customer: null,
      isAgreeTermsConditions: false,
      latest_charge: null,
      howDidYouHearAboutUs: '',
      agent: '',
      leadId: this.$route.query.leadId ?? null,
      // isPayWithMoto: this.isPhonePage ? true : false,
      isPhonePage: false,
      // class3Appeal: false,
      // hasNINONow: false,
      // paymentAfterJan: false,
      // isSplit: false,
      // isDelay: false,
      // delayDate: null,
      customerId: null,
      isFoundApplication: false,
      todayDate: new Date().toISOString().slice(0, 10),
      countryList: [
        {
          name: 'Canada',
          isTax: false,
          image: require('@/assets/canada.png')
        },
        {
          name: 'Ireland/Europe',
          isTax: true,
          image: require('@/assets/europe.png')
        },
        {
          name: 'Australia',
          isTax: false,
          image: require('@/assets/australia.png')
        },
        {
          name: 'USA/Rest of World',
          isTax: false,
          image: require('@/assets/usa.png')
        },
        {
          name: 'South Africa',
          isTax: false,
          image: require('@/assets/south-africa.png')
        },
        {
          name: 'New Zealand',
          isTax: false,
          image: require('@/assets/new-zealand.png')
        }
      ],
      yesNoOptions: ['Yes', 'No'],
      formValid: false,
      formRules: {
        email: [(v) => !!v || 'Email required', (v) => /.+@.+\..+/.test(v) || 'E-mail must be valid'],
        terms: [(v) => !!v || 'Terms required']
      }
    };
  },
  mounted() {
    if (this.foundApplication?.applicationId && this.foundApplication?.currentAddress?.countryCode) {
      this.handleSelectCountry({ name: this.foundApplication.currentAddress.countryCode });
      this.customerId = this.foundApplication?.customerId ? this.foundApplication.customerId : null;
      this.email = this.foundApplication?.email ?? '';
      this.isFoundApplication = true;
    }
    if (this.$route.name === 'Phone Page') {
      this.agent = 'Mike';
      this.isPhonePage = true;
    }
  },
  methods: {
    ...mapActions('main', ['showSnackbar']),
    formatAmount(amount) {
      return amount / 100;
    },
    async handleSelectCountry(country) {
      this.isCountrySelected = true;
      this.tax = country.isTax;
      this.countrySelected = country.name;
      this.showStripeLoading();
      try {
        // eslint-disable-next-line no-undef
        this.stripe = await Stripe(process.env.VUE_APP_STRIPE_PUBLIC_KEY);
        await this.hideStripeLoading();
        this.createAndMountFormElements();
      } catch (err) {
        console.log(err);
        this.showSnackbar({ text: err?.message || 'Oops, Something Went Wrong', color: 'red' });
      }
    },
    showStripeLoading() {
      this.isStripeLoaded = false;
    },
    hideStripeLoading() {
      this.isStripeLoaded = true;
    },
    async createAndMountFormElements() {
      const elements = this.stripe.elements();
      this.cardElement = elements.create('card', {
        hidePostalCode: true
      });
      this.cardElement.mount('#card-element');
      let options = {
        mode: 'billing',
        fields: {
          phone: 'always',
          name: 'auto'
        },
        display: {
          name: 'split'
        },
        validation: {
          phone: {
            required: 'always'
          }
        }
      };
      let defaultValues = {
        firstName: '',
        lastName: '',
        phone: '',
        address: {
          line1: '',
          city: '',
          state: '',
          country: '',
          postal_code: ''
        }
      };

      if (this.foundApplication?.firstName) {
        defaultValues.firstName = this.foundApplication?.firstName;
      }
      if (this.foundApplication?.lastName) {
        defaultValues.lastName = this.foundApplication?.lastName;
      }
      if (this.foundApplication?.phone) {
        defaultValues.phone = this.foundApplication?.phone;
      }
      if (this.foundApplication?.currentAddress) {
        defaultValues.address.line1 = this.foundApplication?.currentAddress?.street ?? '';
        defaultValues.address.city = this.foundApplication?.currentAddress?.city ?? '';
        defaultValues.address.state = this.foundApplication?.currentAddress?.state ?? '';
        defaultValues.address.country = this.foundApplication.currentAddress?.countryCode ?? '';
        defaultValues.address.postal_code = this.foundApplication.currentAddress?.postalCode ?? '';
      } else {
        const countryCode = this.formatCountryNameToCountryCode(this.countrySelected);
        if (countryCode) {
          defaultValues.address.country = countryCode;
        }
      }

      options = {
        ...options,
        defaultValues: defaultValues
      };
      this.addressElement = elements.create('address', options);
      this.addressElement.mount('#address-element');

      this.cardElement.on('change', this.setValidationCardError);
      this.addressElement.on('change', this.setValidationError);
    },
    setValidationCardError(event) {
      this.stripeValidationError = event.error ? event.error.message : '';
    },
    setValidationError(event) {
      const { error, address, name, firstName, lastName, phone } = event.value;
      this.stripeValidationError = error ? error?.message : '';
      if (event.complete) {
        this.shippingAddress = address;
        this.fullName = name;
        this.firstName = firstName;
        this.lastName = lastName;
        this.phone = phone;
      } else {
        this.shippingAddress = null;
        this.fullName = null;
        this.firstName = null;
        this.lastName = null;
        this.phone = null;
      }
      if (address?.country) {
        this.tax = this.isEuropeanCountry(address.country);
        this.countrySelected = address.country;
      }
    },
    async pay() {
      try {
        if (this.formValid === true) {
          if (this.fullName && this.phone && this.shippingAddress && this.email) {
            this.showPayLoading();
            if (!this.isFoundApplication) {
              const { status, isExist, applicationNumbers } = await applicationsService.checkApplicationBeforePayment({
                email: this.email,
                firstName: this.firstName,
                lastName: this.lastName
              });
              console.log('Result Check Application Before Payment:', { status, isExist, applicationNumbers });
              if (isExist && applicationNumbers?.length) {
                this.showSnackbar({
                  text: 'Application(s) already exists with Number(s): ' + applicationNumbers.join(', '),
                  color: 'red'
                });
                this.hidePayLoading();
                return;
              }
            }
            const result = await this.stripe.createPaymentMethod({
              type: 'card',
              card: this.cardElement,
              billing_details: {
                name: this.fullName,
                email: this.email,
                phone: this.phone,
                address: this.shippingAddress
              }
            });

            this.stripePaymentMethodHandler(result);
          } else {
            this.stripeValidationError = 'Fill in your personal data!';
          }
        }
      } catch (err) {
        this.hidePayLoading();
        console.error(err);
        this.showSnackbar({ text: err?.message || 'Oops, Something Went Wrong', color: 'red' });
      }
    },
    async stripePaymentMethodHandler(result) {
      try {
        const { error } = result;
        if (error) {
          this.stripeValidationError = error?.message ?? '';
          this.hidePayLoading();
        } else {
          const res = await StripeService.sendFormData({
            payment_method_id: result.paymentMethod.id,
            customerId: this.customerId,
            currency: this.currencyCode,
            amount: this.amount,
            email: this.email,
            setup_future_usage: false,
            shiping_details: {
              name: this.fullName,
              phone: this.phone,
              address: this.shippingAddress
            },
            isMoto: this.isPhonePage ? true : false,
            howDidYouHearAboutUs: this.howDidYouHearAboutUs,
            agent: this.agent,
            leadId: this.leadId
            // class3Appeal: this.class3Appeal,
            // paymentAfterJan: this.paymentAfterJan,
            // hasNINONow: this.hasNINONow,
            // isSplit: this.isSplit,
            // isDelay: this.isDelay,
            // delayDate: this.delayDate
          });
          const paymentResponse = res;
          this.handleServerResponse(paymentResponse);
        }
      } catch (err) {
        this.hidePayLoading();
        console.error(err);
        this.showSnackbar({ text: err?.message || 'Oops, Something Went Wrong', color: 'red' });
      }
    },
    async handleServerResponse(response) {
      try {
        const { error, requires_action, payment_intent_client_secret } = response;
        if (error) {
          this.showSnackbar({ text: this.stripeValidationError, color: 'red' });
        } else if (requires_action) {
          const { error: errorAction, paymentIntent } = await this.stripe.handleCardAction(
            payment_intent_client_secret
          );
          if (errorAction) {
            // this.stripeValidationError = response.error.message;
          } else {
            const serverResponse = await await StripeService.sendFormData({
              payment_intent_id: paymentIntent.id
            });
            this.handleServerResponse(await serverResponse);
          }
        } else {
          const { id, status, paymentMethod, customer, latest_charge } = response;
          console.log('Payment ID', id);
          console.log('Status', status);
          console.log('Customer', customer);
          console.log('Latest charge', latest_charge);
          this.customer = customer;
          this.latest_charge = latest_charge;
          if (status === 'Success' && id && paymentMethod) {
            this.isPayCompleted = true;
            this.payCompletedId = id;
            if (this.$route.name !== 'Phone Page') {
              if (this.isFoundApplication) {
                this.updateApplication(id, paymentMethod, customer);
              } else {
                this.createApplication(id, paymentMethod, customer);
              }
            }
          }
        }
      } catch (err) {
        console.error(err);
        this.showSnackbar({ text: err?.message || 'Oops, Something Went Wrong', color: 'red' });
      } finally {
        this.hidePayLoading();
      }
    },
    async createApplication(id, paymentMethod, customer) {
      this.showCreateAppLoading();
      try {
        const { status } = await applicationsService.createApplication({
          id: id,
          foundApplication: this.foundApplication ?? null,
          amount: this.amount,
          currencyCode: this.currencyCode,
          email: this.email,
          name: this.fullName,
          firstName: this.firstName,
          lastName: this.lastName,
          phone: this.phone,
          address: {
            street: this.shippingAddress.line1 + (this.shippingAddress.line2 ? ', ' + this.shippingAddress.line2 : ''),
            postalCode: this.shippingAddress.postal_code ?? '',
            state: this.shippingAddress.state ?? '',
            city: this.shippingAddress.city ?? '',
            country: this.shippingAddress.country ?? ''
          },
          paymentMethod: paymentMethod,
          customer: customer ?? null,
          latest_charge: this.latest_charge,
          howDidYouHearAboutUs: this.howDidYouHearAboutUs,
          agent: this.agent
        });
        if (status === 'success') {
          this.isAppCreateError = false;
        } else {
          this.isAppCreateError = true;
        }
      } catch (err) {
        console.error(err);
        this.isAppCreateError = true;
        this.showSnackbar({ text: err?.message || 'Oops, Something Went Wrong', color: 'red' });
      } finally {
        this.hideCreateAppLoading();
      }
    },
    async updateApplication(id, paymentMethod, customer) {
      this.showCreateAppLoading();
      try {
        const { status } = await applicationsService.createPaymentMethod({
          id: id,
          applicationId: this.foundApplication.applicationId,
          amount: this.amount,
          currencyCode: this.currencyCode,
          email: this.email,
          name: this.fullName,
          firstName: this.firstName,
          lastName: this.lastName,
          phone: this.phone,
          address: {
            street: this.shippingAddress.line1 + (this.shippingAddress.line2 ? ', ' + this.shippingAddress.line2 : ''),
            postalCode: this.shippingAddress.postal_code ?? '',
            state: this.shippingAddress.state ?? '',
            city: this.shippingAddress.city ?? '',
            country: this.shippingAddress.country ?? ''
          },
          paymentMethod: paymentMethod,
          customer: customer ?? null,
          latest_charge: this.latest_charge,
          isPartnerXPClient: this.foundApplication.isPartnerXPClient,
          howDidYouHearAboutUs: this.howDidYouHearAboutUs,
          agent: this.agent
        });
        if (status === 'success') {
          this.isAppCreateError = false;
        } else {
          this.isAppCreateError = true;
        }
      } catch (err) {
        console.error(err);
        this.isAppCreateError = true;
        this.showSnackbar({ text: err?.message || 'Oops, Something Went Wrong', color: 'red' });
      } finally {
        this.hideCreateAppLoading();
      }
    },
    showPayLoading() {
      this.isPayLoading = true;
    },
    hidePayLoading() {
      this.isPayLoading = false;
    },
    showCreateAppLoading() {
      this.isAppCreateLoading = true;
    },
    hideCreateAppLoading() {
      this.isAppCreateLoading = false;
    },
    isEuropeanCountry(countryCode) {
      const europeanCountryCodes = [
        'AL',
        'AD',
        'AM',
        'AT',
        'AZ',
        'BY',
        'BE',
        'BA',
        'BG',
        'CH',
        'CY',
        'CZ',
        'DE',
        'DK',
        'EE',
        'ES',
        'FI',
        'FR',
        'GE',
        'GR',
        'HR',
        'HU',
        'IE',
        'IS',
        'IT',
        'KZ',
        'LI',
        'LT',
        'LU',
        'LV',
        'MC',
        'MD',
        'ME',
        'MK',
        'MT',
        'NL',
        'NO',
        'PL',
        'PT',
        'RO',
        'RU',
        'RS',
        'SE',
        'SI',
        'SK',
        'SM',
        'TR',
        'UA',
        'UK',
        'VA',
        'GI',
        'JE',
        'GG'
      ];
      return europeanCountryCodes.includes(countryCode.toUpperCase());
    },
    formatCountryNameToCountryCode(countryName) {
      let countryCode = '';
      switch (countryName) {
        case 'Ireland/Europe':
          countryCode = 'IE';
          break;
        case 'Canada':
          countryCode = 'CA';
          break;
        case 'USA/Rest of World':
          countryCode = 'US';
          break;
        case 'Australia':
          countryCode = 'AU';
          break;
        case 'New Zealand':
          countryCode = 'NZ';
          break;
        case 'South Africa':
          countryCode = 'ZA';
          break;
        default:
          countryCode = '';
      }
      return countryCode;
    }
  }
};
</script>
